import React, {ReactElement, useCallback, useEffect} from 'react';
import {Link, Tab, Tabs, Typography, useTheme} from '@mui/material';
import {useSnackbar} from 'notistack';

import EncryptUtils from 'src/utils/EncryptUtils';
import {getPageData} from '@shared/services/service-handlers/PrivateService';
import {useAppContext} from '@context/AppContext';
import {useLocation, useParams} from 'react-router-dom';
import {HiOutlineExternalLink} from 'react-icons/hi';
import FormConstants from '@shared/constants/FormConstants';
import {useModuleContext} from '@context/ModuleContext';
import {
  MOVE_TO_TAB,
  CHILD_TAB_NAME,
  PARAMS_KEY,
  LOCAL_SEARCH
} from 'src/routes/BaseRoute';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';

export interface IRoutesDataType {
  IsExternalLinkneeded: boolean;
  sub_sections: any;
  sub_section_type: string;
  id: string;
  label: string;
  labelKey?: string;
  route: string;
  count?: number;
  name?: string;
  end_tab_linked?: boolean;
  // icon?: React.ReactNode;
}

interface routeProps {
  row: any;
  selectedTab: string;
  routesData: Array<IRoutesDataType>;
  initialTab?: string;
  action(name: string): void;
  // eslint-disable-next-line react/boolean-prop-naming
  borderBottom?: boolean;
  specimen_number_id?: any;
  specimen_number?: any;
  specimenType?: string;
  procedure_id?: any;
  demographic_id?: any;
  checkedCount?: any;
}

interface ExternalLinkType {
  ExternalLink: string | null;
}

const SubTabsComponent = (props: routeProps): ReactElement => {
  const theme = useTheme();
  const [value, setValue] = React.useState<string | undefined>(
    props?.initialTab
  );
  const {
    appState: {applicationName}
  } = useAppContext();
  const {
    moduleState: {isBH, isBHReason}
  } = useModuleContext();
  const params = useParams();
  const location = useLocation();
  const [externalLink, SetExternalLink] = React.useState<ExternalLinkType>({
    ExternalLink: null
  });
  const {enqueueSnackbar} = useSnackbar();
  const checkedCount = props.checkedCount;
  const externalinkButton = props.routesData.filter(
    data => data.name === 'external_link'
  );
  const [button] = React.useState<boolean>(
    externalinkButton.length === 1 ? true : false
  );

  const moveToTab = EncryptUtils.getDecryptedData(
    location?.search,
    MOVE_TO_TAB
  );
  const childTabName = EncryptUtils.getDecryptedData(
    location?.search,
    CHILD_TAB_NAME
  );
  const localSearch = EncryptUtils.getDecryptedData(
    location?.search,
    LOCAL_SEARCH
  );
  const handleChange = (event: any, newValue: string) => {
    setValue(newValue);

    if (typeof props?.action === 'function') {
      props.action(newValue);
    }
  };

  const changeURLParams = useCallback((tabValue: string) => {
    let localSearchData = localSearch?.length ? JSON.parse(localSearch) : null;
    if (
      localSearchData &&
      tabValue?.toLowerCase() === localSearchData.Level2?.toLowerCase()
    ) {
      const params = new URLSearchParams(location.search);
      localSearchData = {
        ...localSearchData,
        nested_ID: localSearchData.Level2ID
      };
      const encrypted = params.get(PARAMS_KEY);
      const decryptedParams = EncryptUtils.getDecryptedParams(encrypted);
      const newEncrypted = EncryptUtils.setEncryptedParams({
        ...decryptedParams,
        [LOCAL_SEARCH]: JSON.stringify(localSearchData)
      });

      const currentUrl = window.location.href;
      const urlObj = new URL(currentUrl);
      urlObj.searchParams.set(PARAMS_KEY, newEncrypted);
      window.history.replaceState(
        null,
        '',
        urlObj.pathname + '?' + urlObj.searchParams.toString()
      );
    }
  }, []);

  // Effect to call props.action(value) only once on page mount or reload
  // useEffect(() => {
  //   let tabValue = moveToTab || value;
  //   if (childTabName) {
  //     const data = localStorage.getItem('invoice_number_fetched');
  //     const childTab = props?.routesData?.find(
  //       item => item.name === childTabName
  //     );
  //     if (childTab?.end_tab_linked && data) {
  //       tabValue = childTabName;
  //     }
  //   }
  //   if (typeof props?.action === 'function' && tabValue) {
  //     props.action(tabValue);
  //     changeURLParams(tabValue);
  //   }
  // }, [moveToTab]);

  useEffect(() => {
    let tabValue = moveToTab || value;

    // If childTabName exists in current routesData, prefer that
    const childTabMatch = props.routesData?.find(
      item => item.name === childTabName
    );

    if (childTabMatch) {
      tabValue = childTabName;
    } else if (!tabValue) {
      // fallback to first tab if no valid value
      tabValue = props.routesData?.[0]?.name;
    }

    if (typeof props?.action === 'function' && tabValue) {
      setValue(tabValue); // Set the selected tab
      props.action(tabValue);
      changeURLParams(tabValue);
    }
  }, [moveToTab, childTabName]);

  useEffect(() => {
    if (button === true) {
      const externalLinkObject = props?.routesData.filter(
        item => item.name === 'external_link'
      );
      const sql_data = externalLinkObject[0].sub_sections[0].sql_data;
      const newValue = props?.row;
      const moduleName = params?.module_name || '';
      const submoduleName = params?.submodule_name || '';

      const fetchData = async () => {
        try {
          const values = {
            sql_data: sql_data,
            OrderID: newValue.OrderID,
            OrderName: newValue.OrderName,
            OrderDate: newValue.OrderDate,
            legacyApp: newValue.legacyApp,
            legacyMRN: newValue.legacyMRN,
            EncounterDate: newValue.EncounterDate,
            EncounterNumber: newValue.EncounterNumber,
            isBH_selected:
              !isBHReason?.length && isBH == false ? 'true' : isBH.toString(),
            isBH_reason: isBHReason,
            application_name: applicationName
          };
          const encryptRequestData = EncryptUtils.encryptedData(values);
          const res = await getPageData(
            applicationName,
            moduleName,
            submoduleName,
            {
              tk: encryptRequestData
            }
          );
          if (res.data.length === 0) {
            SetExternalLink({
              ExternalLink: null
            });
          } else {
            SetExternalLink(res.data[0]);
          }
        } catch (e) {
          const errMessage = getErrorMessage(e);
          enqueueSnackbar(errMessage, SnackBarConfig.getError());
        }
        // Do something with the response if needed
      };
      fetchData();
    }
  }, [button]);

  const checkInitialValue = (): void => {
    setValue(props?.initialTab);
  };

  React.useEffect(checkInitialValue, [props?.initialTab]);

  return (
    <Tabs
      value={value}
      onChange={handleChange}
      sx={{
        mb: {xs: 3, md: 2}
      }}
      TabIndicatorProps={{
        style: {
          backgroundColor: theme.palette.primary.lighter
        }
      }}>
      {props?.routesData?.map((item, index) => {
        // Hardcoded this tab label for LIS
        const isPathologyReport =
          params?.module_name === FormConstants.MODULE.LIS &&
          params?.submodule_name === FormConstants.SUB_MODULE.RESULTS &&
          props?.specimenType === 'Pathology' &&
          (item?.label === 'Report' || item?.labelKey === 'Report');

        const showTabCount =
          params?.module_name === FormConstants.MODULE.CLINICAL &&
          params?.submodule_name === FormConstants.SUB_MODULE.REPORTS &&
          (item?.label === 'Documents' || item?.labelKey === 'Documents') &&
          checkedCount &&
          parseInt(checkedCount, 10)>-1;
        const tabLabel = isPathologyReport
          ? 'Textual Report'
          : showTabCount
            ? `${item?.label} (${checkedCount})`
            : item?.label || item?.labelKey;

        if (item.sub_section_type === 'button') {
          if (item.IsExternalLinkneeded) {
            return externalLink?.ExternalLink !== null ? (
              <Link
                href={externalLink?.ExternalLink}
                target="_blank"
                rel="noopener"
                color="inherit"
                underline="none"
                sx={{
                  marginLeft: '2rem',
                  marginTop: '0.6rem',
                  flexDirection: 'row',
                  display: 'flex',
                  ...(externalLink?.ExternalLink !== null && {
                    '&:hover': {
                      color: theme.palette.primary.lighter
                    }
                  })
                }}>
                <HiOutlineExternalLink
                  style={{
                    marginTop: '7px',
                    marginRight: '3px'
                  }}
                />
                <Typography
                  sx={{
                    padding: '3px',
                    fontSize: '13px !important',
                    marginTop: '3px',
                    color: '#637381',
                    flexWrap: 'nowrap',
                    fontWeight: theme.typography['fontWeightSemiBold'],
                    '&:hover': {
                      color: theme.palette.primary.lighter
                    }
                  }}>
                  {item.label}
                </Typography>
              </Link>
            ) : (
              <Link
                rel="noopener"
                color="inherit"
                underline="none"
                sx={{
                  marginLeft: '2rem',
                  marginTop: '0.6rem',
                  flexDirection: 'row',
                  display: 'flex'
                }}>
                <HiOutlineExternalLink
                  style={{
                    marginTop: '7px',
                    marginRight: '3px'
                  }}
                />
                <Typography
                  sx={{
                    padding: '3px',
                    fontSize: '12px !important',
                    marginTop: '3px',
                    color: '#637381',
                    flexWrap: 'nowrap',
                    fontWeight: theme.typography['fontWeightSemiBold']
                  }}>
                  {item.label}
                </Typography>
              </Link>
            );
          } else {
            // If IsExternalLinkneeded is false, don't render the button
            return null;
          }
        } else {
          return (
            <Tab
              key={item?.id || index}
              label={tabLabel}
              value={item?.name}
              // icon={item?.icon}
              wrapped
              sx={{
                color: theme.palette.primary.lighter
              }}
            />
          );
        }
      })}
    </Tabs>
  );
};
export default SubTabsComponent;
